import React from "react";
import AppModal from "components/modal/AppModal";
import { useAuth } from "context/AuthContext";
import { gmailDteInboxApi } from "api/endpoints";
import { useIdioma } from "stores/idiomaStore";

const INPUT_BASE =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 transition focus:border-brand-500 focus:outline-none dark:border-white/15 dark:bg-navy-900 dark:text-white";
const BTN_PRIMARY =
  "rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300";
const BTN_SECONDARY =
  "rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800";

const AGE_DAYS_MAX = 8;

function formatCLP(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString("es-CL")}`;
}

function onlyDigits(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function clampDays(value) {
  const digits = onlyDigits(value);
  if (digits === "") return "";
  return String(Math.min(Number(digits), AGE_DAYS_MAX));
}

/**
 * Configuración (solo admin) de los umbrales de ACEPTACIÓN MANUAL de compras.
 * Vive dentro de un modal (no a la vista) por ser sensible. Un DTE recibido se
 * acepta automático salvo que supere el monto o la antigüedad configurados.
 */
export default function ComprasAcceptanceSettings() {
  const { t } = useIdioma();
  const { isReadOnly } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [ageDays, setAgeDays] = React.useState("");
  const [defaults, setDefaults] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { res, data } = await gmailDteInboxApi.getIntercambioSettings();
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      const s = data?.settings || {};
      setAmount(String(s.manual_accept_threshold_amount ?? ""));
      setAgeDays(clampDays(String(s.max_emission_age_days ?? "")));
      setDefaults(s.defaults || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar la configuración.");
    } finally {
      setLoading(false);
    }
  }, []);

  function handleOpen() {
    setNotice("");
    setError("");
    setOpen(true);
    load().catch(() => {});
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { res, data } = await gmailDteInboxApi.updateIntercambioSettings({
        manual_accept_threshold_amount: Number(onlyDigits(amount) || 0),
        max_emission_age_days: Number(clampDays(ageDays) || 0),
      });
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      const s = data?.settings || {};
      setAmount(String(s.manual_accept_threshold_amount ?? ""));
      setAgeDays(clampDays(String(s.max_emission_age_days ?? "")));
      setNotice("Configuración guardada.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar la configuración.");
    } finally {
      setSaving(false);
    }
  }

  if (isReadOnly) return null;

  const footer = (
    <>
      <button type="button" className={BTN_SECONDARY} onClick={() => setOpen(false)} disabled={saving}>
        Cerrar
      </button>
      <button type="button" className={BTN_PRIMARY} onClick={handleSave} disabled={loading || saving}>
        {saving ? "Guardando…" : "Guardar configuración"}
      </button>
    </>
  );

  return (
    <div className="mb-3 flex justify-end">
      <button
        type="button"
        onClick={handleOpen}
        className={BTN_SECONDARY}
        title="Editar los umbrales de aceptación manual de compras"
      >
        ⚙ {t("accion.aceptacionManual")}
      </button>

      <AppModal
        isOpen={open}
        onClose={() => setOpen(false)}
        size="md"
        title={t("accion.aceptacionManual")}
        footer={footer}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Un DTE recibido se acepta automáticamente, salvo que <strong>supere</strong> alguno de
            estos límites: ahí queda marcado para que lo <strong>aceptes manualmente</strong>.
          </p>

          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando…</p>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                  Monto máximo para aceptar automático
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(onlyDigits(e.target.value))}
                  className={INPUT_BASE}
                  placeholder="Ej: 1000000"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Sobre {formatCLP(amount || 0)} → aceptación manual.
                  {defaults ? ` (por defecto ${formatCLP(defaults.manual_accept_threshold_amount)})` : ""}
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                  Antigüedad máxima (días desde emisión)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={ageDays}
                  onChange={(e) => setAgeDays(clampDays(e.target.value))}
                  className={INPUT_BASE}
                  placeholder="Ej: 8"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Más de {ageDays || 0} días → aceptación manual. Máximo <strong>{AGE_DAYS_MAX} días</strong>,
                  porque el SII acepta las facturas automáticamente a los {AGE_DAYS_MAX} días.
                </p>
              </div>

              {notice ? (
                <div className="rounded-lg bg-green-50 px-3 py-2 text-xs text-green-700 dark:bg-green-400/10 dark:text-green-300">
                  {notice}
                </div>
              ) : null}
              {error ? (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-400/10 dark:text-red-300">
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </AppModal>
    </div>
  );
}
